import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Icon } from "@/components";
import {
    Button,
    FormLayout,
    Heading,
    Input,
    Select,
    Switch,
    Tooltip,
} from "@/ui";
import { useForm, useValidator } from "@/hooks";
import {
    DEFAULT_TREEZ_AUTH_MODE,
    TREEZ_AUTH_MODE_OPTIONS,
    TREEZ_AUTH_MODE_TOOLTIP,
} from "../../config/constant";
import treezStyles from "./TreezForm.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const toSelectOptions = (options = []) =>
    options
        .filter(option => option?.value != null && option.value !== "")
        .map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        }));

const normalizeOrganizationId = value => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    return trimmed ? trimmed : null;
};

const buildCredentialPayload = inputs => ({
    ...inputs,
    organization_id: normalizeOrganizationId(inputs.organization_id),
    auth_mode: inputs.auth_mode || DEFAULT_TREEZ_AUTH_MODE,
});

const UserAccountTreezForm = ({
    isCreate,
    handleCancel,
    app,
    selectCredential,
    setCredentials,
    isUpdating,
    handlerUpdatingOrCreate,
}) => {
    const [isFormReset, resetFormInputs] = useState(isCreate);
    const { inputs, inputChange, setInputs } = useForm();

    const authModeOptions = useMemo(
        () => toSelectOptions(TREEZ_AUTH_MODE_OPTIONS),
        [],
    );

    const isPartnerCertAuth =
        (inputs.auth_mode || DEFAULT_TREEZ_AUTH_MODE) === "PARTNER_CERT";

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "dispensary_name",
                method: "isEmpty",
                validWhen: false,
                message: "Dispensary name is required.",
            },
            {
                field: "store_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Store id is required.",
            },
            {
                field: "store_name",
                method: "isEmpty",
                validWhen: false,
                message: "This Store name is required.",
            },
            ...(!isPartnerCertAuth
                ? [
                      {
                          field: "client_id",
                          method: "isEmpty",
                          validWhen: false,
                          message: "This Client id is required.",
                      },
                      {
                          field: "api_key",
                          method: "isEmpty",
                          validWhen: false,
                          message: "This Api key is required.",
                      },
                  ]
                : []),
        ],
        {
            ...inputs,
        },
    );

    useEffect(() => {
        if (!isCreate) {
            const objSelect = app?.credentials[selectCredential];
            setInputs({
                active: objSelect?.active,
                default_store: objSelect?.default_store,
                organization_id: objSelect?.organization_id ?? null,
                dispensary_name: objSelect?.dispensary_name,
                store_id: objSelect?.store_id,
                store_name: objSelect?.store_name,
                client_id: objSelect?.client_id,
                api_key: objSelect?.api_key,
                headless_client_id: objSelect?.headless_client_id,
                headless_client_secret: objSelect?.headless_client_secret,
                auth_mode: objSelect?.auth_mode ?? DEFAULT_TREEZ_AUTH_MODE,
            });
        }
    }, []);

    if (!isCreate && !isFormReset) {
        resetFormInputs(true);
    }

    const createOrUpdate = async () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            let accounts = _.cloneDeep(app.credentials);
            const payload = buildCredentialPayload(inputs);

            if (inputs?.default_store) {
                accounts = accounts.map(account => ({
                    ...account,
                    default_store: false,
                }));
            }

            if (isCreate) {
                accounts.push(payload);
            } else {
                accounts[selectCredential] = payload;
            }
            setCredentials(accounts);
            handlerUpdatingOrCreate(accounts);
        }
    };

    const handleClose = () => {
        handleCancel();
        resetFormInputs(false);
    };

    const handleToggleActive = e => {
        const isChecked = e.target.checked;

        setInputs({
            ...inputs,
            active: isChecked,
            default_store: isChecked ? inputs.default_store : false,
        });
    };

    const handleAuthModeChange = value => {
        setInputs({
            ...inputs,
            auth_mode: value,
        });
    };

    return (
        <div className={treezStyles.credentialForm}>
            <Heading level={5}>
                {isCreate ? "Add Credential" : "Update Credential"}
            </Heading>

            <FormLayout>
                <FormLayout.Section title="Store Informations">
                    <Switch
                        checked={Boolean(inputs.active)}
                        label="Active"
                        name="active"
                        onChange={handleToggleActive}
                    />
                    <Switch
                        checked={Boolean(inputs.default_store)}
                        disabled={!inputs.active}
                        label="Default Select Store"
                        name="default_store"
                        onChange={inputChange}
                    />
                    <FormLayout.Grid columns={3}>
                        <Input
                            fullWidth
                            label="Organization Id"
                            name="organization_id"
                            onChange={inputChange}
                            placeholder="Organization id"
                            value={inputs.organization_id ?? ""}
                        />
                        <Input
                            error={fieldError(validator, "store_id")}
                            fullWidth
                            label="Store id"
                            name="store_id"
                            onChange={inputChange}
                            placeholder="Store id"
                            value={inputs.store_id || ""}
                        />
                        <Input
                            error={fieldError(validator, "store_name")}
                            fullWidth
                            label="Store name"
                            name="store_name"
                            onChange={inputChange}
                            placeholder="Store name"
                            value={inputs.store_name || ""}
                        />
                    </FormLayout.Grid>
                </FormLayout.Section>

                <FormLayout.Section title="Treez Credentials">
                    <FormLayout.Grid columns={3}>
                        <Input
                            error={fieldError(validator, "dispensary_name")}
                            fullWidth
                            label="Dispensary Name"
                            name="dispensary_name"
                            onChange={inputChange}
                            placeholder="Dispensary name"
                            value={inputs.dispensary_name || ""}
                        />
                        <Input
                            error={fieldError(validator, "client_id")}
                            fullWidth
                            label="Client Id"
                            name="client_id"
                            onChange={inputChange}
                            placeholder="Client id"
                            value={inputs.client_id || ""}
                        />
                        <Input
                            error={fieldError(validator, "api_key")}
                            fullWidth
                            label="Api Key"
                            name="api_key"
                            onChange={inputChange}
                            placeholder="Api key"
                            value={inputs.api_key || ""}
                        />
                    </FormLayout.Grid>
                </FormLayout.Section>

                <FormLayout.Section title="Treez Headless">
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Input
                            fullWidth
                            label="Headless Client Id"
                            name="headless_client_id"
                            onChange={inputChange}
                            placeholder="Headless client id"
                            value={inputs.headless_client_id || ""}
                        />
                        <Input
                            fullWidth
                            label="Headless Client Secret"
                            name="headless_client_secret"
                            onChange={inputChange}
                            placeholder="Headless client secret"
                            value={inputs.headless_client_secret || ""}
                        />
                    </FormLayout.Grid>
                </FormLayout.Section>

                <FormLayout.Section title="Treez Authentication">
                    <FormLayout.Grid columns={3}>
                        <Select
                            fullWidth
                            label="Authentication Mode"
                            onValueChange={handleAuthModeChange}
                            options={authModeOptions}
                            value={inputs.auth_mode ?? DEFAULT_TREEZ_AUTH_MODE}
                        />
                        <div className={treezStyles.authModeHint}>
                            <Tooltip
                                content={TREEZ_AUTH_MODE_TOOLTIP}
                                side="top"
                                triggerHelpCursor
                                variant="multi"
                            >
                                <span
                                    className={treezStyles.infoTrigger}
                                    tabIndex={0}
                                >
                                    <Icon icon="info" />
                                </span>
                            </Tooltip>
                        </div>
                    </FormLayout.Grid>
                </FormLayout.Section>
            </FormLayout>

            <div className={treezStyles.credentialActions}>
                <Button
                    disabled={isUpdating}
                    onClick={handleClose}
                    type="button"
                    variant="secondary"
                >
                    Cancel
                </Button>
                <Button
                    loading={isUpdating}
                    onClick={createOrUpdate}
                    type="button"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

export default UserAccountTreezForm;
